import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import CommonFallback from 'components/CommonFallback';
import ErrorBoundary from 'components/ErrorBoundary';
import { MovieHeading, MovieSection } from 'components/Movies';
import { handle404, handle301, handle500 } from 'lib/pageHelpers';
import { isServer } from 'lib/isBrowser';
import ProgramsByProgramTypeQuery from 'queries/programs/programsByProgramTypeQuery.query';
import ProgramsByGuidQuery from 'queries/programs/programsByGuidQuery.query';
import PromotionCollectionQuery from 'queries/overview/overviewPromotionCollection.query';
import OverviewModel from 'models/overview.model';
import MovieModel from 'models/movie.program.model';
import { IGetInitialProps } from 'typings/IGetInitialProps';
import { IWithInitialProps } from 'typings/IWithInitialProps';
import { MoviesWrapperStyle } from 'components/Movies/Movies.style';
import AllMoviesQuery from 'queries/programs/allMovies.query';
import { FALLBACK_UI_LABELS, PROGRAM, PROMO_QUERY_CLIENT, FIXED_COUNT } from '../../../src/client/constants';

const FILMS_SLUG = 'films';

const Movies: IWithInitialProps<{}> = ({ viewMode, client }) => {
  const router = useRouter();
  const videoId = router.query.videoId ? `${router.query.videoId}` : null;
  const [isHeaderHidden, setIsHeaderHidden] = useState<boolean>(false);

  useEffect(() => {
    let ScrollPosition: number = window.scrollY;
    const handleScroll = (): void => {
      setIsHeaderHidden(window.scrollY > 80 && window.scrollY >= ScrollPosition);
      ScrollPosition = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const [movie, setMovie] = useState<MovieModel | null>(null);
  useQuery<{
    programs: { items: IProgramGraphql[]; totalResults: number };
  }>(ProgramsByProgramTypeQuery, {
    variables: {
      guid: videoId,
      programTypes: PROGRAM.MOVIE
    },
    skip: !videoId || movie?.guid === videoId,
    onCompleted: data => {
      if (data.programs.items.length) {
        setMovie(new MovieModel(data.programs.items[0]));
      }
    }
  });

  return (
    <>
      <Head>
        {movie && videoId ? (
          <>
            <title>{movie.programFullTitle}</title>
            <meta name="og:title" content={movie.programFullTitle} />
            <meta name="og:type" content="video:tv_show" />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(movie.structuredData)
              }}
            />
            <meta name="description" content={movie.description} />
            <meta name="og:description" content={movie.description} />
            <meta name="og:url" content={`https://www.kijk.nl${router.asPath}`} />
            <meta name="og:image" content={movie.image} />
          </>
        ) : (
          <title>Kijk de beste films op KIJK.nl</title>
        )}
      </Head>
      <MoviesWrapperStyle isHeaderHidden={isHeaderHidden} data-testid="movies">
        <ErrorBoundary onError={_ => <CommonFallback message={FALLBACK_UI_LABELS.MOVIES} />}>
          <MovieHeading videoId={videoId} viewMode={viewMode} client={client} />
          <MovieSection viewMode={viewMode} />
        </ErrorBoundary>
      </MoviesWrapperStyle>
    </>
  );
};

Movies.getInitialProps = async ({ apolloClient, query }: IGetInitialProps): Promise<any> => {
  if (!isServer) return {};

  try {
    await apolloClient.query<{ programs: { items: IProgramGraphql[] } }>({
      query: AllMoviesQuery,
      variables: {
        limit: FIXED_COUNT.ALL_MOVIES_LIMIT
      }
    });
  } catch (ignore) {}
  // if this is a video route
  if (query.videoId) {
    try {
      const { data } = await apolloClient.query<{ programs: { items: IProgramGraphql[] } }>({
        query: ProgramsByGuidQuery,
        variables: {
          guid: query.videoId
        }
      });
      if (!data?.programs?.items?.length) {
        return handle404(`video doesn't exist`);
      }

      const { slug, guid, type } = data.programs.items[0];

      if (type !== PROGRAM.MOVIE) {
        return handle404(`video doesn't exist`);
      }
      if (query.videoSlug !== slug) {
        const path = `/films/${slug}/${guid}`;
        return handle301(path);
      }
    } catch (error) {
      return handle404(`video doesn't exist`);
    }
  }

  try {
    const { data } = await apolloClient.query<{ overview: IEditorialOverviewData }>({
      query: PromotionCollectionQuery,
      variables: {
        slug: FILMS_SLUG,
        client: PROMO_QUERY_CLIENT.DESKTOP
      }
    });
    const overview = new OverviewModel(data.overview, {
      isOnFormatPage: false
    });

    if (!overview.isValid) {
      return query.videoSlug ? handle404(`Overview is not valid`) : {};
    }
  } catch {
    return handle500('Could not fetch overview');
  }
};

export default Movies;
